// ══════════════════════════════════════════════════════════════════
//  낮 ↔ 밤 이음매 — **짐을 넘기는 일만 한다.**
//
//  ■ 규칙은 여기 한 줄도 없다. 낮↔밤 변환은 이미 `day/nightio.js` 가 다 갖고 있다:
//        toNightOpts(state, nightNo) — 낮이 밤에 보내는 짐
//        toNightResult(world)        — 밤이 낮에 보내는 성적
//    그 둘을 **헤드리스 프로브가 전투 밤 35개 완주로 증명**한다(probe_campaign·probe_loop).
//    없던 것은 **브라우저에서 두 화면 사이를 건너는 길**뿐이고, 이 파일이 그 길이다.
//
//  ■ **왜 파일을 새로 팠나.** 낮 디오라마와 밤 게임을 각각 다른 세션이 만지고 있다.
//    이음매를 그쪽 파일에 흩어 놓으면 셋이 같은 줄을 두고 부딪친다. 그래서
//    이음매는 여기 한 곳에 모으고, 양쪽 파일에는 **부르는 줄만** 남긴다.
//
//  ■ 정본이 못 박은 것 — 디자인방향 71행:
//        *"**낮의 종료가 곧 밤의 시작이다** — 일기를 덮으면 화면이 어두워지고,
//          다음에 보이는 것은 이미 밤 필드다. **사이 화면 없음.**"*
//    그러므로 이 파일은 **로딩 화면도 결과 화면도 만들지 않는다.** 암전 한 겹뿐이다.
//    밤 성적은 **다음 아침 젖틀의 우유통**이 말한다(5단 ↔ 손실 정산표 5구간 1:1).
//
//  ■ **한 번에 통째로 쓴다.** 조각으로 나눠 쓰면 중간에 끊겼을 때 하루가 증발한다.
//    다 만든 뒤 한 번의 setItem 으로 넣고, 꺼낼 때 체크섬으로 온전한지 본다.
//
//  ■ **버전 도장을 찍는다.** 모양이 바뀌면 옛 기록을 조용히 먹지 않고 버린다 —
//    조용히 먹으면 「왜 이 밤이 이상하지」가 되고, 원인을 못 찾는다.
// ══════════════════════════════════════════════════════════════════

const KEY = 'nn.handoff';
const V = 1;

// 저장은 **탭 하나의 생애**만 산다. 캠페인 이어하기는 다른 문제이고 정본 미기재다 —
// 여기서 지어내지 않는다. 탭을 닫으면 그 회차는 거기서 끝난다.
const store = () => {
  try { return globalThis.sessionStorage ?? null; } catch { return null; }
};

// 아주 작은 체크섬(FNV-1a 32bit). 암호가 아니라 **잘려 들어왔는지** 보는 것이다.
function sum(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16);
}

function write(rec) {
  const s = store();
  if (!s) return { ok: false, why: 'sessionStorage 를 못 쓴다' };
  const body = JSON.stringify(rec);
  try {
    s.setItem(KEY, JSON.stringify({ v: V, sum: sum(body), body }));
    return { ok: true };
  } catch (e) {
    return { ok: false, why: `저장 실패 — ${e?.message ?? e}` };
  }
}

// 읽는다. **의심스러우면 버린다.** 반쯤 맞는 기록으로 하루를 진행시키지 않는다.
function read() {
  const s = store();
  if (!s) return { ok: false, why: 'sessionStorage 를 못 쓴다' };
  const raw = s.getItem(KEY);
  if (!raw) return { ok: false, why: 'none' };          // 정상 — 넘어온 짐이 없다
  let outer;
  try { outer = JSON.parse(raw); } catch { return { ok: false, why: '기록이 깨졌다(JSON)' }; }
  if (outer.v !== V) return { ok: false, why: `버전이 다르다 (기록 v${outer.v} · 지금 v${V})` };
  if (sum(outer.body) !== outer.sum) return { ok: false, why: '체크섬 불일치 — 잘려 들어왔다' };
  try { return { ok: true, rec: JSON.parse(outer.body) }; }
  catch { return { ok: false, why: '기록이 깨졌다(본문)' }; }
}

export function clearHandoff() { try { store()?.removeItem(KEY); } catch { /* 없으면 그만 */ } }

// ── 낮 → 밤 ──────────────────────────────────────────────────────
// `closeDiary(s)` 가 낸 것을 그대로 받는다. **여기서 뭘 계산하지 않는다.**
//   opts — toNightOpts 산물(nightKey·weapon·seed·flockN·rescue·dayMods·dogs)
//   save — autosave(s).json. **캠페인 전체가 여기 있다.** 이게 없으면 밤에서 돌아온
//          낮이 자기가 누구였는지 모른다(페이지가 새로 뜨므로 메모리가 다 날아간다).
//   snap — 원장 스냅샷. `openDay(s, res, snap)` 의 셋째 인자다.
export function putToNight({ opts, save, snap }) {
  if (!opts?.nightKey) return { ok: false, why: 'opts.nightKey 가 없다 — 밤을 고를 수 없다' };
  if (!save) return { ok: false, why: 'save 가 없다 — 돌아올 낮이 사라진다' };
  return write({ leg: 'toNight', at: Date.now(), nightKey: opts.nightKey, opts, save, snap: snap ?? null });
}

// 개발용 H 밤 이동은 전투 카드만 바꾸는 것이 아니라 캠페인의 현재 날짜도 옮긴다.
// 그래야 선택한 밤을 마친 뒤 `commitNight`와 `openDay`가 그 다음 날짜를 연다.
export function retargetNightJump({ opts, save, snap }, nightKey) {
  const match = /^N([1-9]|[1-3]\d|40)$/.exec(String(nightKey ?? ''));
  if (!match) return { ok: false, why: `밤 번호가 잘못됐다 — ${nightKey}` };
  if (!save) return { ok: false, why: 'save 가 없다 — 돌아올 낮이 사라진다' };

  let state;
  try { state = JSON.parse(save); }
  catch { return { ok: false, why: '캠페인 저장이 깨졌다(JSON)' }; }
  if (!state?.journey) return { ok: false, why: '캠페인 저장에 journey 가 없다' };

  const nightNo = Number(match[1]);
  state.journey.nightNo = nightNo;
  state.journey.dayNo = nightNo;
  state.journey.tally = nightNo - 1;
  state.journey.kind = 'normal';
  state.journey.sabbathClosed = false;

  return {
    ok: true,
    opts: { ...opts, nightKey: `N${nightNo}` },
    save: JSON.stringify(state),
    snap: snap ?? null,
  };
}

// 밤이 부른다. **짐을 지우지 않는다** — 그 안의 save·snap 을 밤이 끝나고 되돌려줘야 한다.
export function takeToNight() {
  const r = read();
  if (!r.ok) return r;
  if (r.rec.leg !== 'toNight') return { ok: false, why: `이 짐은 밤 것이 아니다 (leg=${r.rec.leg})` };
  return { ok: true, ...r.rec };
}

// ── 밤 → 낮 ──────────────────────────────────────────────────────
// 밤이 끝나고 부른다. **save·snap 은 낮이 보낸 것을 그대로 되돌려준다** —
// 밤은 캠페인을 모르고, 알 필요도 없다(그것이 `nightio` 헤더가 말한 규율이다).
export function putToDay(res) {
  const r = read();
  if (!r.ok) return { ok: false, why: `넘어온 짐이 없다 — ${r.why}` };
  return write({ ...r.rec, leg: 'toDay', at: Date.now(), res: res ?? null });
}

// 낮이 부른다. **꺼내면 지운다** — 새로고침 한 번에 같은 밤이 두 번 정산되면 안 된다.
export function takeToDay() {
  const r = read();
  if (!r.ok) return r;
  if (r.rec.leg !== 'toDay') return { ok: false, why: `아직 밤이 안 끝났다 (leg=${r.rec.leg})` };
  clearHandoff();
  return { ok: true, ...r.rec };
}

// ⚠⚠ **`staticNightResult` 가 여기 있었다** 【40일 개편 2026-08-03】.
//   구 N16·N21(정적 밤)을 「밤을 안 열고 그 자리에서 정산」하던 자리다. 그 두 밤은
//   *"전투 없음 — 안정성 100% 확정 → 우유 만액"* 이었고, **안식일과 같은 물건**이라
//   안식일이 흡수했다. 안식일은 정산 자체를 안 한다(수입 0) — 부를 곳이 없다.
//   ⚠ **반쯤 살려 두지 않는다.** 되살릴 때는 이 주석과 git 이력이 설계를 들고 있다.


// ── 암전 ─────────────────────────────────────────────────────────
// 정본 71행: *"일기를 덮으면 **화면이 어두워지고**, 다음에 보이는 것은 이미 밤 필드다"*.
// 페이지가 갈리는 순간의 **흰 깜빡임**을 이 한 겹이 덮는다. 로딩 화면이 아니다 —
// 글자도 회전자도 없다. 그냥 어두워진다.
const FADE_MS = 420;
// 막의 색 — 저녁은 어둠으로, 아침은 **새벽빛으로** 건넌다【비주얼 폴리싱 2026-08-06】.
//   정본 71행의 「그냥 어두워진다」는 낮→밤 이야기다 — 그 방향은 그대로 DUSK 다.
//   밤→낮은 정본이 색을 못 박지 않았고, 검은 막으로 아침을 여는 것이 더 이상했다.
export const DUSK = '#14100D';
export const DAWN = '#e7d9b8';

export function fadeOut(ms = FADE_MS, color = DUSK) {
  return new Promise((done) => {
    const el = document.createElement('div');
    el.setAttribute('data-nn-fade', 'out');
    el.style.cssText = `position:fixed;inset:0;background:${color};opacity:0;z-index:9999;
      pointer-events:none;transition:opacity ${ms}ms ease-in`;
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    // ⚠ **보험은 조건 없이 터진다.** 합성을 안 하는 패널에서는 transition 이 안 끝나
    //   `transitionend` 가 영영 안 온다 — 그러면 하루가 넘어가지 않는다.
    //   낮이 이미 같은 결함을 겪었다(playClose 의 무조건 타임아웃).
    setTimeout(done, ms + 60);
  });
}

// 들어오는 쪽. 막을 **먼저 깔아 두고** 걷어낸다 — 그래야 첫 프레임이 안 튄다.
export function fadeIn(ms = FADE_MS, color = DUSK) {
  const el = document.createElement('div');
  el.setAttribute('data-nn-fade', 'in');
  el.style.cssText = `position:fixed;inset:0;background:${color};opacity:1;z-index:9999;
    pointer-events:none;transition:opacity ${ms}ms ease-out`;
  document.body.appendChild(el);
  requestAnimationFrame(() => requestAnimationFrame(() => { el.style.opacity = '0'; }));
  setTimeout(() => el.remove(), ms + 120);
}

// ── 길 ───────────────────────────────────────────────────────────
// 두 화면이 서로를 부르는 주소. **한 곳에 적는다** — 흩어 놓으면 갈린다.
export const ROUTE = {
  night: 'night.html?from=day',
  day:   'day.html?from=night',
};
export const cameFrom = (what) => {
  try { return new URLSearchParams(location.search).get('from') === what; }
  catch { return false; }
};

export async function go(url, ms = FADE_MS, color = DUSK) {
  await fadeOut(ms, color);
  location.href = url;
}
